import { skipToken } from "@reduxjs/toolkit/query";
import type {
  PlaybookId,
  PlaybookNode,
} from "@repo/app-types/entities/playbook";
import type { RecordingId } from "@repo/app-types/entities/recording";
import type { RecordingAssessmentFeedback } from "@repo/app-types/entities/recordingAssessmentFeedback";
import { buildTeammateDisplayNames } from "@repo/app-types/entities/teammate";
import { Button, ButtonVariant } from "@repo/ui/Buttons/button";
import { Size } from "@repo/ui/helpers/helpers";
import { Textarea } from "@repo/ui/Inputs/textarea";
import { ModalCloseButton } from "@repo/ui/Modal/modalCloseButton";
import { SimpleSelect } from "@repo/ui/Select/simpleSelect";
import { type FC, type FormEvent, useState } from "react";
import { Dialog, Header, Heading } from "react-aria-components";
import {
  usePostRecordingAssessmentFeedbackMutation,
  useUpdateRecordingAssessmentFeedbackMutation,
} from "../../api/entities/recording.js";
import { useBasicTeammateQuery } from "../../api/entities/teammate.js";

export interface GradingAssessmentFeedbackDialogProps {
  playbookId: PlaybookId;
  node: PlaybookNode;
  recordingId: RecordingId;
  grade: {
    value: string;
    commentary: string;
  };
  feedback: RecordingAssessmentFeedback | undefined;
}

export const GradingAssessmentFeedbackDialog: FC<
  GradingAssessmentFeedbackDialogProps
> = (props) => {
  const { node, feedback } = props;
  const [correctValue, setCorrectValue] = useState<string | null>(
    props.feedback?.data.correct_value ?? null,
  );
  const [explanation, setExplanation] = useState<string>(
    props.feedback?.data.explanation ?? "",
  );

  // Look up the feedback author's name
  const { data: feedbackAuthor } = useBasicTeammateQuery(
    feedback ? feedback.teammate_id : skipToken,
  );
  const feedbackAuthorName = feedbackAuthor
    ? buildTeammateDisplayNames(feedbackAuthor).primaryDisplayName
    : undefined;

  const isValid = correctValue !== null;

  const [postFeedback, { isLoading: isPosting }] =
    usePostRecordingAssessmentFeedbackMutation();

  const [updateFeedback, { isLoading: isUpdating }] =
    useUpdateRecordingAssessmentFeedbackMutation();

  const isLoading = isPosting || isUpdating;

  const handleSubmit = async (event: FormEvent, close: () => void) => {
    event.preventDefault();

    if (!isValid) {
      return;
    }

    const feedbackNode =
      node.type === "section"
        ? {
            id: node.id,
            name: node.name,
            description: node.description,
            grading_value_type: "section" as const,
          }
        : node;

    const feedbackData = {
      criteria_node: feedbackNode,
      grading: props.grade,
      correct_value: correctValue,
      explanation,
    };

    if (props.feedback) {
      // Update existing feedback
      await updateFeedback({
        recordingId: props.recordingId,
        playbookId: props.playbookId,
        feedbackId: props.feedback.id,
        data: feedbackData,
      });
    } else {
      // Create new feedback
      await postFeedback({
        recordingId: props.recordingId,
        playbookId: props.playbookId,
        data: feedbackData,
      });
    }

    close();
  };

  const renderCorrectScoreField = () => {
    if (node.type === "section") {
      return (
        <SimpleSelect
          label="Expected result"
          placeholder="Does the section apply"
          options={[
            {
              label: "Section applies to this recording",
              value: "a",
            },
            {
              label: "Section does not apply",
              value: "n/a",
            },
          ]}
          value={correctValue}
          onChange={setCorrectValue}
        />
      );
    }

    switch (node.grading_value_type) {
      case "yes_no":
        return (
          <SimpleSelect
            label="What is the correct grade?"
            placeholder="Yes / No / Not applicable"
            options={[
              {
                label: "Yes",
                value: "yes",
              },
              {
                label: "No",
                value: "no",
              },
              {
                label: "Not applicable",
                value: "n/a",
              },
            ]}
            value={correctValue}
            onChange={setCorrectValue}
          />
        );

      case "1_5_score":
        return (
          <SimpleSelect
            label="What is the correct score?"
            placeholder="1 to 5 / Not applicable"
            options={[
              {
                label: "1 (Very poor)",
                value: "1",
              },
              {
                label: "2 (Poor)",
                value: "2",
              },
              {
                label: "3 (Acceptable)",
                value: "3",
              },
              {
                label: "4 (Good)",
                value: "4",
              },
              {
                label: "5 (Excellent)",
                value: "5",
              },
              {
                label: "Not applicable",
                value: "n/a",
              },
            ]}
            value={correctValue}
            onChange={setCorrectValue}
          />
        );
    }
  };

  return (
    <Dialog className="w-80 space-y-2 p-4">
      {({ close }) => (
        <>
          <Header className="flex items-center justify-between">
            <Heading slot="title" className="font-bold">
              AI grading feedback
            </Heading>

            <ModalCloseButton />
          </Header>

          {feedback && feedbackAuthorName && (
            <div className="text-xs text-muted-foreground">
              {feedbackAuthorName} on{" "}
              {new Date(feedback.created_at).toLocaleDateString("en-US", {
                month: "short",
                day: "numeric",
                year: "numeric",
              })}
            </div>
          )}

          <form
            className="space-y-2"
            onSubmit={(event) => handleSubmit(event, close)}
          >
            <div>{renderCorrectScoreField()}</div>

            <div>
              <Textarea
                label="Explanation"
                placeholder="Provide a comment to help improve the AI grading"
                value={explanation}
                onChange={setExplanation}
                height="5rem"
              />
            </div>

            <div className="flex justify-end space-x-2">
              <Button
                type="submit"
                variant={ButtonVariant.Primary}
                size={Size.MEDIUM}
                isDisabled={!isValid || isLoading}
                isLoading={isLoading}
              >
                Save
              </Button>
            </div>
          </form>
        </>
      )}
    </Dialog>
  );
};
